import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Download,
  HelpCircle,
  Hourglass,
  Import,
  Pencil,
  Pin,
  Plus,
  RefreshCw,
  Rss,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn, formatDateTime } from '@/lib/utils';
import { fmtLocalDateTime } from '@/lib/dateTimeLocal';
import { legacyRoutes } from '@/lib/legacy';
import { getAccountSensors, exportTransmitterConnections, refreshAccountSensorsFromMongo, pinSensor, unpinSensor } from '../api';
import type { GetAccountSensorsParams } from '../api';
import {
  normalizeSensorStatus,
  SENSOR_STATUS_FILTER_OPTIONS,
} from '../lib/sensorStatus';
import { EditSensorDialog } from '@/features/operations-sensors/components/EditSensorDialog';
import { TransmitterEditModal } from '@/features/operations-transmitters/components/TransmitterEditModal';
import { CreateSensorDialog } from '@/features/operations-sensors/components/CreateSensorDialog';
import { CalibrationCertificateDialog } from '@/features/intelligence/components/CalibrationCertificateDialog';
import { StreamViewDialog } from './StreamViewDialog';
import ReassignDialog from './ReassignDialog';
import ImportSensorsModal from './ImportSensorsModal';
import { AccountsColumnsMenu } from './AccountsColumnsMenu';
import { AccountsColumnResizeHandle } from './AccountsColumnResizeHandle';
import { useColumnState } from '@/hooks/useColumnState';
import type { SensorDetail, AccountMetrics } from '../types';
import { AccountDetailModal } from './detail/AccountDetailModal';
import { SensorDetailBody } from './detail/SensorDetailBody';

// Module-level selection store — survives React 18 StrictMode remounts.
// Keyed by accountId so multiple account tabs don't bleed into each other.

// =============================================================================
// Column Definition
// =============================================================================

interface SensorRowHelpers {
  onReassign: (sensor: SensorDetail) => void;
  onEdit: (sensorId: string) => void;
  /** Opens the read-only detail panel for this sensor (name-cell click). */
  onSelect: (sensor: SensorDetail) => void;
  onImageClick: (url: string, name: string) => void;
  onTransmitterClick: (transmitterMongoId: string) => void;
  onCalibrationClick: (calibrationMongoId: string) => void;
  onStreamClick: (streamMongoId: string) => void;
  onPinChange: (sensor: SensorDetail, pin: boolean) => void;
  /** PG sensor.id currently being pinned or unpinned — disables that row's checkbox. */
  pinningSensorId: string | undefined;
  accountId: string;
  /** Mongo _id of the currently selected (detail-panel-open) sensor. */
  selectedSensorId: string | null;
}

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: GetAccountSensorsParams['sortKey'];
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T, helpers: SensorRowHelpers) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  noExport?: boolean;
  className?: string;
}

// Page size = 1000. Almost every account fits in a single page (typical fleet
// is <500 sensors); the rare giants (MAPAQ, very-large hospital networks) get
// Previous/Next pagination controls below the table.
const PAGE_SIZE = 1000;

// Icon map mirroring the V2 customer-app sensor legend (see
// apps/frontend/atek/components/dashboard/dashboard_legend.html). The status
// column shows the same icon next to the label so the table reads at a glance.
//   ? (purple)        = Unmanaged / Alert Condition Not Set
//   rss (green)       = Active Sensor Within Safe Range
//   chevron-down      = Reading Below Safe Range
//   chevron-up        = Reading Above Safe Range
//   hourglass         = Notifications Inhibited
//   clock             = Communication Lost (Inactive)
//   wrench            = Sensor in Maintenance
//   alert-triangle    = Event to Acknowledge
//   archive           = Archived
const SENSOR_STATUS_ICONS: Record<number, LucideIcon> = {
  0: HelpCircle,
  1: Rss,
  2: ChevronDown,
  3: ChevronUp,
  4: AlertTriangle,
  5: Clock,
  6: Wrench,
  8: Hourglass,
  9: Archive,
};

function resolveStatusIcon(raw: number | string | null | undefined): LucideIcon | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const code = typeof raw === 'number' ? raw : /^\d+$/.test(String(raw).trim()) ? Number(raw) : null;
  return code !== null ? SENSOR_STATUS_ICONS[code] || null : null;
}

// Status-filter dropdown: render each option as a coloured badge with its icon
// so the dropdown reads exactly like the rows it filters.
// Filter values match SENSOR_STATUS_FILTER_OPTIONS.value (string keys).
const STATUS_FILTER_ICONS: Record<string, LucideIcon> = {
  unmanaged: HelpCircle,
  active: Rss,
  pending_setup: HelpCircle,
  inactive: Clock,
  in_alert: AlertTriangle,
  to_acknowledge: AlertTriangle,
  maintenance: Wrench,
  inhibited: Hourglass,
  archived: Archive,
};

function renderStatusFilterOption(opt: { label: string; value: string }) {
  const descriptor = normalizeSensorStatus(opt.value);
  const Icon = STATUS_FILTER_ICONS[opt.value] || null;
  return (
    <Badge variant={descriptor.variant} className="inline-flex items-center gap-1">
      {Icon && <Icon className="h-3 w-3" />}
      {opt.label}
    </Badge>
  );
}
const FILTER_STORAGE_KEY = 'atek-account-dashboard-sensors-column-filters-v2';
const VISIBILITY_STORAGE_KEY = 'atek-account-dashboard-sensors-column-visibility-v2';
const ORDER_STORAGE_KEY = 'atek-account-dashboard-sensors-column-order-v2';
const WIDTHS_STORAGE_KEY = 'atek-account-dashboard-sensors-column-widths-v2';
const DEFAULT_COLUMN_WIDTH = 160;

const COLUMNS: ColumnDef<SensorDetail>[] = [
  {
    key: 'hasPhoto',
    label: 'Image',
    noExport: true,
    filterType: 'select',
    filterOptions: [
      { label: 'With Photo', value: 'with' },
      { label: 'Without Photo', value: 'without' },
    ],
    getValue: (sensor) => (sensor.image?.url ? 'with' : 'without'),
    render: (sensor, { onImageClick }) =>
      sensor.image?.url ? (
        <img
          src={sensor.image.url}
          alt={sensor.name}
          className="h-12 w-16 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
          loading="lazy"
          onClick={(e) => {
            e.stopPropagation();
            onImageClick(sensor.image!.url!, sensor.name);
          }}
        />
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'id',
    label: 'ID',
    sortKey: 'id',
    filterType: 'text',
    getValue: (sensor) => sensor.customid || sensor._id.slice(-6),
    render: (sensor) => (
      <span className="font-mono text-sm text-muted-foreground block truncate">
        {sensor.customid || sensor._id.slice(-6)}
      </span>
    ),
  },
  {
    key: 'name',
    label: 'Name',
    sortKey: 'name',
    filterType: 'text',
    getValue: (sensor) => sensor.name,
    render: (sensor, { onSelect, onEdit, selectedSensorId }) => (
      <div className="flex items-center gap-1 min-w-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(sensor);
          }}
          className={cn(
            'text-left text-atek-blue hover:underline truncate flex-1 min-w-0',
            selectedSensorId === sensor._id && 'font-medium',
          )}
          title={`View sensor detail: ${sensor.name}`}
        >
          {sensor.name}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(sensor._id);
          }}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          title={`Edit sensor: ${sensor.name}`}
          aria-label={`Edit ${sensor.name}`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    sortKey: 'status',
    filterType: 'select',
    filterOptions: [...SENSOR_STATUS_FILTER_OPTIONS],
    getValue: (sensor) => {
      const status = normalizeSensorStatus(sensor.status);
      if (status.key === 'active' && !sensor.stream_name && !sensor.stream?.name) {
        return 'pending_setup';
      }
      return status.key;
    },
    render: (sensor) => {
      const status = normalizeSensorStatus(sensor.status);
      const hasStream = !!(sensor.stream_name || sensor.stream?.name);
      if (status.key === 'active' && !hasStream) {
        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge variant="warning" className="cursor-help text-sm normal-case font-medium">
                  <HelpCircle className="h-3.5 w-3.5 mr-1" />Pending Setup
                </Badge>
              </TooltipTrigger>
              <TooltipContent>
                <p>This sensor is marked as active but has no stream connected yet.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
      const Icon = resolveStatusIcon(sensor.status);
      return (
        <Badge variant={status.variant} className="inline-flex items-center gap-1 text-sm normal-case font-medium">
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {status.label}
        </Badge>
      );
    },
  },
  {
    key: 'stream',
    label: 'Stream',
    sortKey: 'stream',
    filterType: 'text',
    getValue: (sensor) => sensor.stream_name || sensor.stream?.name || '',
    render: (sensor, { onStreamClick }) => {
      const name = sensor.stream_name || sensor.stream?.name;
      if (!name) return <span className="text-muted-foreground">—</span>;
      const mongoStreamId = sensor.stream?._id;
      if (!mongoStreamId) {
        // No clickable id — render as plain text matching the link style visually
        return (
          <span className="text-sm truncate block w-full" title={name}>
            {name}
          </span>
        );
      }
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onStreamClick(mongoStreamId);
          }}
          className="text-sm text-atek-blue hover:underline truncate text-left block w-full"
          title={`Open stream: ${name}`}
        >
          {name}
        </button>
      );
    },
  },
  {
    key: 'calibration',
    label: 'Calibration',
    sortKey: 'calibration',
    filterType: 'select',
    filterOptions: [
      { label: 'Overdue', value: 'overdue' },
      { label: 'Due within 90 days', value: 'due-soon' },
      { label: 'OK', value: 'ok' },
      { label: 'None', value: 'none' },
    ],
    getValue: (sensor) => sensor.stream?.calibration?.due
      ? new Date(sensor.stream.calibration.due).toLocaleDateString('en-CA')
      : '',
    render: (sensor, { onCalibrationClick }) => {
      const cal = sensor.stream?.calibration;
      if (!cal?.due) return <span className="text-muted-foreground">—</span>;
      const daysLeft = Math.floor((new Date(cal.due).getTime() - Date.now()) / 86_400_000);
      const label = new Date(cal.due).toLocaleDateString('en-CA');
      const certNum = cal.cert_number;
      const calMongoId = cal.mongoId;
      const variant: 'danger' | 'warning' | 'default' =
        daysLeft < 0 ? 'danger' : daysLeft <= 90 ? 'warning' : 'default';
      const tooltipText =
        daysLeft < 0
          ? `Overdue by ${Math.abs(daysLeft)} days${calMongoId ? ' · Click to open certificate' : ''}`
          : daysLeft <= 90
            ? `Due in ${daysLeft} days${calMongoId ? ' · Click to open certificate' : ''}`
            : calMongoId ? 'Click to open certificate' : 'Calibrated';
      // Badge for overdue/warning; plain text (atek-blue if clickable) when ok.
      const dateNode =
        variant === 'default' ? (
          <span className="text-sm tabular-nums text-atek-blue">{label}</span>
        ) : (
          <Badge variant={variant} className="tabular-nums text-xs normal-case font-medium">{label}</Badge>
        );
      const inner = (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          {dateNode}
          {certNum && (
            <span className="text-xs text-muted-foreground truncate max-w-[80px]" title={certNum}>{certNum}</span>
          )}
        </span>
      );
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {calMongoId ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCalibrationClick(calMongoId);
                  }}
                  className="text-left hover:underline"
                  title={tooltipText}
                >
                  {inner}
                </button>
              ) : (
                <span title={tooltipText}>{inner}</span>
              )}
            </TooltipTrigger>
            <TooltipContent><p>{tooltipText}</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
  },
  {
    key: 'iq',
    label: 'QI',
    filterType: 'select',
    filterOptions: [
      { label: 'Has QI', value: 'yes' },
      { label: 'Missing QI', value: 'no' },
    ],
    getValue: (sensor) => (sensor.installationqualification?._id ? 'yes' : 'no'),
    render: (sensor) => {
      const iqId = sensor.installationqualification?._id;
      if (!iqId) return <Badge variant="secondary" className="text-sm normal-case font-medium">—</Badge>;
      return (
        <a
          href={legacyRoutes.installationQualification(iqId)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <Badge variant="success" className="cursor-pointer hover:opacity-80 text-sm normal-case font-medium">QI ✓</Badge>
        </a>
      );
    },
    align: 'center',
  },
  {
    key: 'value',
    label: 'Value',
    sortKey: 'value',
    filterType: 'number-range',
    getValue: (sensor) => (sensor.value != null ? sensor.value : null),
    render: (sensor) => (
      <span className="text-sm">
        {sensor.value !== undefined && sensor.value !== null
          ? `${sensor.value.toFixed(2)}${sensor.sensortype?.unit ? ` ${sensor.sensortype.unit}` : ''}`
          : '—'}
      </span>
    ),
    align: 'right',
    className: 'tabular-nums',
  },
  {
    key: 'timestamp',
    label: 'Last Update',
    sortKey: 'timestamp',
    filterType: 'date-range',
    getValue: (sensor) => sensor.timestamp || '',
    render: (sensor) => {
      if (!sensor.timestamp) return <span className="text-sm">—</span>;
      return (
        <span className="text-sm tabular-nums" title={formatDateTime(sensor.timestamp)}>
          {fmtLocalDateTime(sensor.timestamp)}
        </span>
      );
    },
  },
  {
    key: 'siteLocation',
    label: 'Site / Location',
    sortKey: 'siteLocation',
    filterType: 'text',
    getValue: (sensor) =>
      [sensor.site?.name, sensor.location?.name].filter(Boolean).join(' / ') || '',
    render: (sensor) => {
      const siteLocation =
        [sensor.site?.name, sensor.location?.name].filter(Boolean).join(' / ');
      if (!siteLocation) return <span className="text-muted-foreground">—</span>;
      return (
        <span className="text-sm truncate block w-full" title={siteLocation}>
          {siteLocation}
        </span>
      );
    },
  },
  {
    key: 'transmitter',
    label: 'Transmitter',
    filterType: 'text',
    // Identify by dev_id (the MAC-style hardware ID printed on the device),
    // not by the friendly name. Sort + filter follow whatever's shown.
    getValue: (sensor) => sensor.transmitter?.devId || sensor.transmitter?.name || '',
    render: (sensor, { onTransmitterClick }) => {
      const tx = sensor.transmitter;
      if (!tx?._id) return <span className="text-muted-foreground">—</span>;
      const display = tx.devId || tx.name || tx._id.slice(-6);
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onTransmitterClick(tx._id);
          }}
          className="text-sm text-atek-blue hover:underline truncate block w-full text-left"
          title={`Open transmitter detail: ${tx.name || tx.devId || tx._id}`}
        >
          {display}
        </button>
      );
    },
  },
  {
    key: 'usergroup',
    label: 'Usergroup',
    filterType: 'text',
    getValue: (sensor) => sensor.usergroup?.name || '',
    render: (sensor) => (
      <span className="text-sm truncate block w-full" title={sensor.usergroup?.name || ''}>
        {sensor.usergroup?.name || '—'}
      </span>
    ),
  },
  {
    key: 'organisation',
    label: 'Organisation',
    filterType: 'text',
    getValue: (sensor) => sensor.organisation?.name || '',
    render: (sensor) => (
      <span className="text-sm truncate block w-full" title={sensor.organisation?.name || ''}>
        {sensor.organisation?.name || '—'}
      </span>
    ),
  },
  {
    key: 'attached',
    label: 'Attached',
    noFilter: true,
    noExport: true,
    getValue: (sensor) => (sensor.accountIdOverridden ? 'pinned' : ''),
    render: (sensor, { onPinChange, pinningSensorId }) => (
      <div className="flex items-center gap-1.5">
        <Checkbox
          checked={!!sensor.accountIdOverridden}
          disabled={pinningSensorId === sensor.id}
          onCheckedChange={(checked) => onPinChange(sensor, checked === true)}
          aria-label={sensor.accountIdOverridden ? 'Unpin sensor from account' : 'Pin sensor to account'}
        />
        {sensor.accountIdOverridden && (
          <Pin className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        )}
      </div>
    ),
    align: 'center',
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (sensor, { onReassign, onEdit }) => (
      <div className="flex items-center gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit(sensor._id);
          }}
          className="text-atek-blue hover:underline text-sm cursor-pointer"
        >
          Edit
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReassign(sensor);
          }}
          className="text-muted-foreground hover:text-foreground cursor-pointer"
          title="Reassign to another account"
        >
          <ArrowRightLeft className="h-4 w-4" />
        </button>
      </div>
    ),
    align: 'right',
  },
];

// =============================================================================
// localStorage Persistence
// =============================================================================

// Column visibility/order/widths persistence is delegated to the generic
// `useColumnState` hook — see src/hooks/useColumnState.ts.

function loadColumnFilters(): Record<string, string> {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // Ignore malformed localStorage
  }
  return {};
}

// =============================================================================
// CSV Export Utilities
// =============================================================================

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = `'${str}`;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(items: SensorDetail[], columns: ColumnDef<SensorDetail>[], filename: string): void {
  const exportCols = columns.filter((c) => !c.noFilter && !c.noExport);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(items: SensorDetail[], columns: ColumnDef<SensorDetail>[]): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter && !c.noExport);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  return navigator.clipboard.writeText([header, ...rows].join('\n'));
}

// =============================================================================
// SortableHeader Component
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;

  return (
    <Button variant="ghost" size="sm" className="-ml-3 h-8 hover:bg-transparent" onClick={() => onSort(sortKey)}>
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter(
  col: ColumnDef<SensorDetail>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {col.key === 'status' ? renderStatusFilterOption(opt) : opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// SensorsTab Component (server-side pagination + filtering)
// =============================================================================

function parseMultiSelectFilter(rawValue?: string): string[] {
  if (!rawValue) return [];

  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
}

function buildServerFilterParams(columnFilters: Record<string, string>): GetAccountSensorsParams {
  const params: GetAccountSensorsParams = {};

  const idSearch = columnFilters.id?.trim();
  if (idSearch) params.idSearch = idSearch;

  const nameSearch = columnFilters.name?.trim();
  if (nameSearch) params.nameSearch = nameSearch;

  const streamSearch = columnFilters.stream?.trim();
  if (streamSearch) params.streamSearch = streamSearch;

  const siteLocationSearch = columnFilters.siteLocation?.trim();
  if (siteLocationSearch) params.siteLocationSearch = siteLocationSearch;

  const statusKeys = parseMultiSelectFilter(columnFilters.status);
  if (statusKeys.length > 0) {
    params.statusKeys = statusKeys.join(',');
  }

  const photoValues = parseMultiSelectFilter(columnFilters.hasPhoto);
  if (photoValues.length === 1 && (photoValues[0] === 'with' || photoValues[0] === 'without')) {
    params.hasPhoto = photoValues[0];
  }

  const [valueMin = '', valueMax = ''] = (columnFilters.value || '').split('>>');
  if (valueMin.trim()) params.valueMin = valueMin.trim();
  if (valueMax.trim()) params.valueMax = valueMax.trim();

  const [lastReadingFrom = '', lastReadingTo = ''] = (columnFilters.timestamp || '').split('>>');
  if (lastReadingFrom) params.lastReadingFrom = lastReadingFrom;
  if (lastReadingTo) params.lastReadingTo = lastReadingTo;

  return params;
}

interface SensorsTabProps {
  accountId: string;
  metrics: AccountMetrics;
  accountName?: string;
  legacyOrganisationId?: string | null;
  canImportSensors?: boolean;
}

export default function SensorsTab({
  accountId,
  metrics,
  accountName = 'Current Account',
  legacyOrganisationId,
  canImportSensors,
}: SensorsTabProps) {
  // Server-side state
  const [page, setPage] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [calibrationCertMongoId, setCalibrationCertMongoId] = useState<string | null>(null);
  const [viewStreamId, setViewStreamId] = useState<string | null>(null);

  // Server-side column filters
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState<Record<string, string>>(loadColumnFilters);

  // Visibility/order/widths managed by the shared hook (src/hooks/useColumnState.ts).
  const {
    orderedColumns,
    columnWidths,
    isVisible,
    toggleColumn,
    setColumnOrder,
    setColumnWidth,
  } = useColumnState<ColumnDef<SensorDetail>>({
    columns: COLUMNS,
    storageKeys: {
      visibility: VISIBILITY_STORAGE_KEY,
      order: ORDER_STORAGE_KEY,
      widths: WIDTHS_STORAGE_KEY,
    },
  });

  const widthFor = (key: string) => columnWidths[key] ?? DEFAULT_COLUMN_WIDTH;

  // One-shot hydration from URL: lets the accounts table deep-link into
  // pre-filtered views (e.g. ?tab=sensors&status=inactive). Consumed params
  // are stripped so reloading the tab doesn't reapply them on top of user edits.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const status = searchParams.get('status');
    const calibration = searchParams.get('calibration');
    if (!status && !calibration) return;
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (status) next.status = JSON.stringify([status]);
      if (calibration) next.calibration = JSON.stringify([calibration]);
      return next;
    });
    const sp = new URLSearchParams(searchParams);
    sp.delete('status');
    sp.delete('calibration');
    setSearchParams(sp, { replace: true });
    // Intentionally mount-only — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [sortKey, setSortKey] = useState<NonNullable<GetAccountSensorsParams['sortKey']>>('timestamp');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  // Modal states
  const [reassignSensor, setReassignSensor] = useState<SensorDetail | null>(null);
  const [editSensorId, setEditSensorId] = useState<string | null>(null);
  const [editTransmitterId, setEditTransmitterId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);

  const sensorCreateOrganisationId = legacyOrganisationId || undefined;
  const hasLegacyOrg = canImportSensors ?? !!sensorCreateOrganisationId;

  // Detail modal state — plain useState (survives React 18 StrictMode's double-mount,
  // which re-runs effects, not state). Toggle: click the open sensor again to close.
  const [selectedSensor, setSelectedSensor] = useState<SensorDetail | null>(null);
  const selectedSensorId = selectedSensor?._id ?? null;

  // Persist column filters to localStorage
  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnFilters(columnFilters);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnFilters]);

  const visibleColumns = orderedColumns.filter((col) => isVisible(col.key));
  const serverFilters = useMemo(
    () => buildServerFilterParams(debouncedColumnFilters),
    [debouncedColumnFilters],
  );

  // Server-side data fetch
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-sensors', accountId, page, sortKey, sortDirection, serverFilters],
    queryFn: () =>
      getAccountSensors(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        sortKey,
        sortDirection,
        ...serverFilters,
      }),
    enabled: !!accountId,
  });

  const rawSensors = data?.data ?? [];
  const total = data?.total ?? 0;

  const calibrationFilterValues = parseMultiSelectFilter(debouncedColumnFilters.calibration);
  const sensors = calibrationFilterValues.length > 0
    ? rawSensors.filter((s) => {
        const due = s.stream?.calibration?.due;
        if (!due) return calibrationFilterValues.includes('none');
        const daysLeft = Math.floor((new Date(due).getTime() - Date.now()) / 86_400_000);
        const status = daysLeft < 0 ? 'overdue' : daysLeft <= 90 ? 'due-soon' : 'ok';
        return calibrationFilterValues.includes(status);
      })
    : rawSensors;

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    } else {
      setSortKey(key as NonNullable<GetAccountSensorsParams['sortKey']>);
      setSortDirection(1);
    }
  };

  const handleRefreshFromMongo = async () => {
    if (!accountId) return;
    setIsRefreshing(true);
    try {
      const { updated, total: mongoTotal } = await refreshAccountSensorsFromMongo(accountId);
      await refetch();
      toast.success(`Refreshed ${updated}/${mongoTotal} sensors from MongoDB`);
    } catch (e) {
      console.error('Refresh from Mongo failed:', e);
      toast.error('Failed to refresh sensors from MongoDB');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleExportConnections = async () => {
    if (!accountId) return;

    try {
      setIsExporting(true);
      const blob = await exportTransmitterConnections(accountId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transmitter-connections-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Export failed:', e);
      toast.error('Failed to export transmitter connections');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllSensors = async () => {
    try {
      setIsExportingAll(true);
      const allSensors: SensorDetail[] = [];
      const batchSize = 100;
      let pageNum = 0;
      let totalCount = 0;

      do {
        const batch = await getAccountSensors(accountId, {
          pageNumber: pageNum,
          pageLength: batchSize,
          sortKey,
          sortDirection,
          ...serverFilters,
        });
        allSensors.push(...(batch.data || []));
        totalCount = batch.total ?? 0;
        pageNum++;
      } while (allSensors.length < totalCount);

      exportToCSV(allSensors, COLUMNS, 'account-sensors-all');
      toast.success(`Exported ${allSensors.length} sensors`);
    } catch (e) {
      console.error('Export all sensors failed:', e);
      toast.error('Failed to export all sensors');
    } finally {
      setIsExportingAll(false);
    }
  };

  const queryClient = useQueryClient();

  const pinMutation = useMutation({
    mutationFn: (sensorId: string) => pinSensor(accountId, sensorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-sensors', accountId] });
      toast.success('Sensor pinned to account');
    },
    onError: () => toast.error('Failed to pin sensor'),
  });

  const unpinMutation = useMutation({
    mutationFn: (sensorId: string) => unpinSensor(accountId, sensorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-sensors', accountId] });
      toast.success('Sensor unpinned from account');
    },
    onError: () => toast.error('Failed to unpin sensor'),
  });

  // Per-row in-flight guard: the sensor whose pin/unpin is currently in flight.
  // Variables are the plain sensorId string passed to mutate().
  const pinningSensorId: string | undefined =
    pinMutation.isPending ? pinMutation.variables :
    unpinMutation.isPending ? unpinMutation.variables :
    undefined;

  // Row helpers for column renderers
  const rowHelpers: SensorRowHelpers = {
    onReassign: setReassignSensor,
    onEdit: (id: string) => setEditSensorId(id),
    onSelect: (s: SensorDetail) => setSelectedSensor((prev) => (prev?._id === s._id ? null : s)),
    onImageClick: (url, name) => setLightbox({ url, name }),
    onTransmitterClick: (mongoTransmitterId: string) => setEditTransmitterId(mongoTransmitterId),
    onCalibrationClick: (calibrationMongoId: string) => setCalibrationCertMongoId(calibrationMongoId),
    onStreamClick: (streamMongoId: string) => setViewStreamId(streamMongoId),
    onPinChange: (sensor: SensorDetail, pin: boolean) => {
      const id = sensor.id;
      if (!id) return;
      if (pin) {
        pinMutation.mutate(id);
      } else {
        unpinMutation.mutate(id);
      }
    },
    pinningSensorId,
    accountId,
    selectedSensorId,
  };

  if (!accountId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Sensors</CardTitle>
          <CardDescription>View sensors for this account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">
            Unable to load sensors - account ID not available
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading sensors</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" onClick={() => refetch()}>
              Try Again
            </Button>
            <a href={legacyRoutes.sensors()}>
              <Button variant="outline">View in Legacy System</Button>
            </a>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>
              <button
                type="button"
                onClick={() => setColumnFilter('status', '')}
                className="hover:underline"
              >
                {metrics?.total_sensors ?? total} Sensors
              </button>
            </CardTitle>
            {/* Customer-app status breakdown — same numbers and colors as the
                V2 customer dashboard (apps/frontend/atek/components/dashboard).
                Each colored label is clickable: it sets the status column
                filter so the table updates to just that subset. The total
                count is rendered as the CardTitle above so this description
                only shows status buckets. */}
            <div className="mt-1 text-sm font-medium space-y-1 text-muted-foreground">
              {/* Line 1: operational lifecycle. Every bucket is rendered as a
                  persistent pill-tab — even at 0 — so the filter is always
                  reachable and the dashboard reads like a fixed legend. Zero
                  buckets dim slightly to keep visual hierarchy without
                  hiding the chip. */}
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  onClick={() => setColumnFilter('status', JSON.stringify(['active']))}
                  className={cn(
                    'rounded-md border border-transparent px-2 py-0.5 text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:border-emerald-700 dark:hover:bg-emerald-950',
                    (metrics?.active_sensors ?? 0) === 0 && 'opacity-60',
                  )}
                >
                  {metrics?.active_sensors ?? 0} Active
                </button>
                <button
                  type="button"
                  onClick={() => setColumnFilter('status', JSON.stringify(['inactive']))}
                  className={cn(
                    'rounded-md border border-transparent px-2 py-0.5 text-gray-500 hover:border-gray-300 hover:bg-gray-100 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:bg-gray-800',
                    (metrics?.inactive_sensors ?? 0) === 0 && 'opacity-60',
                  )}
                >
                  {metrics?.inactive_sensors ?? 0} Inactive
                </button>
                <button
                  type="button"
                  onClick={() => setColumnFilter('status', JSON.stringify(['maintenance']))}
                  className={cn(
                    'rounded-md border border-transparent px-2 py-0.5 text-gray-400 hover:border-gray-300 hover:bg-gray-100 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:bg-gray-800',
                    (metrics?.maintenance_sensors ?? 0) === 0 && 'opacity-60',
                  )}
                >
                  {metrics?.maintenance_sensors ?? 0} In Maintenance
                </button>
                <button
                  type="button"
                  onClick={() => setColumnFilter('status', JSON.stringify(['archived']))}
                  className={cn(
                    'rounded-md border border-transparent px-2 py-0.5 text-slate-600 hover:border-slate-300 hover:bg-slate-100 dark:text-slate-300 dark:hover:border-slate-600 dark:hover:bg-slate-800',
                    (metrics?.archived_sensors ?? 0) === 0 && 'opacity-60',
                  )}
                >
                  {metrics?.archived_sensors ?? 0} Archived
                </button>
              </div>
              {/* Line 2: alert lifecycle (in alert / to acknowledge / inhibited / unmanaged) */}
              <div className="flex flex-wrap items-center gap-1">
                <button
                  type="button"
                  onClick={() => setColumnFilter('status', JSON.stringify(['in_alert']))}
                  className={cn(
                    'rounded-md border border-transparent px-2 py-0.5 text-red-600 hover:border-red-300 hover:bg-red-50 dark:text-red-400 dark:hover:border-red-700 dark:hover:bg-red-950',
                    (metrics?.in_alarm_sensors ?? 0) === 0 && 'opacity-60',
                  )}
                >
                  {metrics?.in_alarm_sensors ?? 0} In Alert
                </button>
                <button
                  type="button"
                  onClick={() => setColumnFilter('status', JSON.stringify(['to_acknowledge']))}
                  className={cn(
                    'rounded-md border border-transparent px-2 py-0.5 text-yellow-600 hover:border-yellow-300 hover:bg-yellow-50 dark:text-yellow-400 dark:hover:border-yellow-700 dark:hover:bg-yellow-950',
                    (metrics?.to_acknowledge_sensors ?? 0) === 0 && 'opacity-60',
                  )}
                >
                  {metrics?.to_acknowledge_sensors ?? 0} To Acknowledge
                </button>
                <button
                  type="button"
                  onClick={() => setColumnFilter('status', JSON.stringify(['inhibited']))}
                  className={cn(
                    'rounded-md border border-transparent px-2 py-0.5 text-orange-600 hover:border-orange-300 hover:bg-orange-50 dark:text-orange-400 dark:hover:border-orange-700 dark:hover:bg-orange-950',
                    (metrics?.inhibited_sensors ?? 0) === 0 && 'opacity-60',
                  )}
                >
                  {metrics?.inhibited_sensors ?? 0} Alerts Inhibited
                </button>
                <button
                  type="button"
                  onClick={() => setColumnFilter('status', JSON.stringify(['unmanaged']))}
                  className={cn(
                    'rounded-md border border-transparent px-2 py-0.5 text-purple-600 hover:border-purple-300 hover:bg-purple-50 dark:text-purple-400 dark:hover:border-purple-700 dark:hover:bg-purple-950',
                    (metrics?.unmanaged_sensors ?? 0) === 0 && 'opacity-60',
                  )}
                >
                  {metrics?.unmanaged_sensors ?? 0} Unmanaged
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshFromMongo}
                    disabled={isRefreshing || !accountId}
                  >
                    <RefreshCw className={cn('h-4 w-4 mr-1', isRefreshing && 'animate-spin')} />
                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p>Pull the latest <strong>value</strong>, <strong>last reading time</strong>, and <strong>status</strong> from MongoDB for every sensor on this account — the same source the customer-facing app reads. Bypasses CDC drift.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                copyToClipboard(sensors, COLUMNS);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExportingAll}>
                  <Download className="h-4 w-4 mr-1" />
                  {isExportingAll ? 'Exporting...' : 'CSV'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export Sensors</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    exportToCSV(sensors, COLUMNS, 'account-sensors');
                    toast.success(`Exported ${sensors.length} rows`);
                  }}
                >
                  Export Page ({sensors.length} rows)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllSensors}>
                  Export All Sensors ({total})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Columns menu — drag-to-reorder + visibility, matches the main accounts table. */}
            <AccountsColumnsMenu
              orderedColumns={orderedColumns}
              isVisible={isVisible}
              onToggle={toggleColumn}
              onReorder={setColumnOrder}
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportConnections}
                    disabled={isExporting || !accountId}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {isExporting ? 'Exporting...' : 'Export Connections'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p>Exports how each transmitter connects to the network (WiFi, LTE, LoRaWAN) — not the sensor data shown in the table above. Use the CSV button to export sensor data.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {hasLegacyOrg && (
              <Button
                variant="default"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setIsImportOpen(true)}
              >
                <Import className="h-4 w-4 mr-1" />
                Import Sensors
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setShowCreateDialog(true)}
              disabled={!sensorCreateOrganisationId}
              title={
                !sensorCreateOrganisationId
                  ? 'Cannot create a sensor until this account has a legacy organisation link.'
                  : undefined
              }
            >
              <Plus className="h-4 w-4 mr-1" />
              New Sensor
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table
            // table-layout: fixed + explicit colgroup so resized widths apply
            // to every row (filter row, header row, body rows), not just whichever
            // row defines them. Mirrors the accounts list table.
            style={{
              tableLayout: 'fixed',
              width: `${visibleColumns.reduce((sum, c) => sum + widthFor(c.key), 0)}px`,
            }}
          >
            <colgroup>
              {visibleColumns.map((col) => (
                <col key={col.key} style={{ width: `${widthFor(col.key)}px` }} />
              ))}
            </colgroup>
            <TableHeader>
              {/* ROW 1: Column filters (always visible) */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) =>
                  renderColumnFilter(col, columnFilters, setColumnFilter),
                )}
              </TableRow>

              {/* ROW 2: Column labels + sort + resize handles */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(
                      // Match the compact body padding so the column rhythm
                      // stays consistent.
                      'relative py-1.5 px-2',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                    )}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                    {!col.noFilter && (
                      <AccountsColumnResizeHandle
                        columnKey={col.key}
                        currentWidth={widthFor(col.key)}
                        onResize={setColumnWidth}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading && Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Empty state */}
              {!isLoading && sensors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                    No sensors found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading && sensors.map((sensor) => (
                <TableRow
                  key={sensor._id}
                  className={cn(
                    'hover:bg-muted/50',
                    selectedSensorId === sensor._id && 'ring-2 ring-inset ring-primary/40 bg-primary/5',
                  )}
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={`${sensor._id}-${col.key}`}
                      className={cn(
                        // overflow-hidden + min-w-0 prevent cell contents from
                        // bleeding past the column width set by the colgroup
                        // (table-layout: fixed). Without it, long stream/name
                        // strings would push the column wider on render.
                        'text-sm py-1.5 px-2 overflow-hidden align-top min-w-0',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.align === 'center' && 'text-center',
                        col.className,
                      )}
                    >
                      {col.render
                        ? col.render(sensor, rowHelpers)
                        : (String(col.getValue(sensor) ?? '') || '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>{/* /overflow-x-auto */}

        {/* Sensor detail modal — plain Dialog open state, no resize wiring needed */}
        <AccountDetailModal
          open={!!selectedSensor}
          onOpenChange={(next) => { if (!next) setSelectedSensor(null); }}
          title={selectedSensor?.name ?? ''}
          subtitle={selectedSensor ? [
            selectedSensor.sensortype?.name,
            selectedSensor.site?.name,
          ].filter(Boolean).join(' · ') : undefined}
          badgeLabel={selectedSensor ? normalizeSensorStatus(selectedSensor.status).label : undefined}
          badgeVariant={selectedSensor ? normalizeSensorStatus(selectedSensor.status).variant : undefined}
        >
          {selectedSensor && (
            <SensorDetailBody sensor={selectedSensor} open={!!selectedSensor} />
          )}
        </AccountDetailModal>

        {/* Pagination — only render when the account exceeds one page. */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-4 px-4 text-sm text-muted-foreground">
            <div>
              Page {page + 1} of {Math.ceil(total / PAGE_SIZE)} · Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || isLoading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={(page + 1) * PAGE_SIZE >= total || isLoading}
              >
                Next
              </Button>
            </div>
          </div>
        )}

      </CardContent>

      {/* Reassign Dialog */}
      <ReassignDialog
        entityType="sensor"
        entityId={reassignSensor?.id || reassignSensor?._id || ''}
        entityLabel={reassignSensor?.name ?? ''}
        currentAccountId={accountId}
        currentAccountName={accountName}
        open={!!reassignSensor}
        onOpenChange={(open) => { if (!open) setReassignSensor(null); }}
      />

      {/* Create Sensor Dialog */}
      <CreateSensorDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        organisationId={sensorCreateOrganisationId}
        requireOrganisation
      />

      {/* Edit Sensor Dialog */}
      <EditSensorDialog
        sensorId={editSensorId}
        open={!!editSensorId}
        onOpenChange={(open) => {
          if (!open) setEditSensorId(null);
        }}
      />

      {/* Calibration Certificate Dialog — opens directly to the cert details
          (number, polynomial, traceability) when the Calibration cell is clicked. */}
      <CalibrationCertificateDialog
        mongoId={calibrationCertMongoId}
        open={!!calibrationCertMongoId}
        onOpenChange={(open) => {
          if (!open) setCalibrationCertMongoId(null);
        }}
      />

      {/* Stream View Dialog — read-only snapshot opened from the Stream
          cell. Mutations live on /operations/streams. */}
      <StreamViewDialog
        mongoStreamId={viewStreamId}
        open={!!viewStreamId}
        onOpenChange={(open) => {
          if (!open) setViewStreamId(null);
        }}
      />

      {/* Transmitter Detail Modal — opened from the Transmitter column. Uses
          the same modal the Transmitters tab opens; passes the Mongo
          organisation id so admin-vs-non-admin dropdown options resolve. */}
      <TransmitterEditModal
        transmitterId={editTransmitterId}
        open={!!editTransmitterId}
        onOpenChange={(open) => {
          if (!open) setEditTransmitterId(null);
        }}
        organisationId={legacyOrganisationId}
      />

      {/* Image Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={(open) => { if (!open) setLightbox(null); }}>
        <DialogContent className="max-w-3xl p-2">
          {lightbox && (
            <img
              src={lightbox.url}
              alt={lightbox.name}
              className="w-full h-auto max-h-[85vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>

      <ImportSensorsModal
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        accountId={accountId}
        accountName={accountName}
      />
    </Card>
  );
}
